import * as React from "react";
import {
  Alert,
  AlertTitle,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  TextField,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useLanguage } from "./Language";
import axios, { AxiosResponse } from "axios";
import { createContext, useContext } from "react";

interface ResetPasswordReturn {
  success: boolean;
}

interface ResetPasswordParameters {
  username: string;
}

export interface ForgotPasswordDialogProps {
  open: boolean;
  onClose: () => void;
}

export const ForgotPasswordDialog: React.FC<ForgotPasswordDialogProps> = ({
  onClose,
  open,
}) => {
  const { translate } = useLanguage();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const [success, setSuccess] = React.useState(false);
  const [username, setUsername] = React.useState("");
  const [usernameInvalid, setUsernameInvalid] = React.useState(true);
  const [usernameHelperText, setUsernameHelperText] = React.useState(" ");
  const [usernameTouched, setUsernameTouched] = React.useState(false);
  const [formSubmitted, setFormSubmitted] = React.useState(false);
  // requiredFieldError must be included in useEffect deps to handle language changes
  const requiredFieldError = translate("requiredFieldError");

  React.useEffect(() => {
    if (usernameInvalid && (usernameTouched || formSubmitted)) {
      setUsernameHelperText(requiredFieldError);
    } else {
      setUsernameHelperText(" ");
    }
  }, [usernameInvalid, usernameTouched, formSubmitted, requiredFieldError]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFormSubmitted(true);
    const form = event.target as HTMLFormElement;
    const errorInputs = form.querySelectorAll('input[aria-invalid="true"]');

    if (errorInputs.length > 0) {
      console.log("Form has invalid fields");
      return;
    }

    try {
      const postData: ResetPasswordParameters = {
        username: username,
      };
      const { data }: AxiosResponse<ResetPasswordReturn> = await axios.post(
        "/account/reset/password",
        postData
      );
    } catch (error) {
      // console.error("Error calling /account/reset/password", error);
    } finally {
      setSuccess(true);
    }
  };

  const onChangeUsername = (event: React.ChangeEvent<HTMLInputElement>) => {
    const username = event.target.value;
    setUsernameInvalid(username.trim().length === 0);
    setUsername(username);
  };

  const onBlurUsername = (event: React.FocusEvent<HTMLInputElement>) => {
    const username = event.target.value;
    setUsernameTouched(true);
    setUsernameInvalid(username.trim().length === 0);
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <Dialog open={open} fullScreen={fullScreen}>
      {success && (
        <React.Fragment>
          <DialogTitle>{translate("forgotPassword")}</DialogTitle>
          <DialogContent>
            <Alert severity="success">
              {translate("requestPasswordResetResponse")}
            </Alert>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>{translate("ok")}</Button>
          </DialogActions>
        </React.Fragment>
      )}

      {!success && (
        <React.Fragment>
          <DialogTitle>{translate("forgotPassword")}</DialogTitle>
          <form onSubmit={handleSubmit} noValidate>
            <DialogContent>
              <DialogContentText>
                {translate("forgotPasswordFormInstructions")}
              </DialogContentText>
              <Stack gap={2} paddingTop={2}>
                <Alert severity="info">
                  <AlertTitle>{translate("forgotUsername")}</AlertTitle>
                  {translate("forgotUsernameMessage")}
                </Alert>
                <TextField
                  name="username"
                  id="forgot-password-username"
                  value={username}
                  autoFocus
                  error={usernameInvalid && (usernameTouched || formSubmitted)}
                  label={translate("ezUsername")}
                  helperText={usernameHelperText}
                  required
                  onChange={onChangeUsername}
                  onBlur={onBlurUsername}
                ></TextField>
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleClose}>{translate("cancel")}</Button>
              <Button type="submit">{translate("submit")}</Button>
            </DialogActions>
          </form>
        </React.Fragment>
      )}
    </Dialog>
  );
};

const ForgotPasswordDialogContext = createContext(null);

export const useForgotPasswordDialog = () => {
  const context = useContext(ForgotPasswordDialogContext);

  if (!context) {
    throw new Error(
      "useForgotPasswordDialog must be used within a ForgotPasswordDialogProvider"
    );
  }

  return context;
};

export const ForgotPasswordDialogProvider = ({
  children,
}: React.PropsWithChildren) => {
  const [open, setOpen] = React.useState<boolean>(false);
  const openDialog = () => {
    setOpen(true);
  };

  const closeDialog = () => {
    setOpen(false);
  };

  const contextValue = {
    open,
    openDialog,
    closeDialog,
  };

  return (
    <ForgotPasswordDialogContext.Provider value={contextValue}>
      {children}
      {/* Conditionally rendering so initial state is reset on open */}
      {open && (
        <ForgotPasswordDialog
          open={open}
          onClose={closeDialog}
        ></ForgotPasswordDialog>
      )}
    </ForgotPasswordDialogContext.Provider>
  );
};
